import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Loader2, FileText, Printer, AlertCircle, X, Truck, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApiService, InvoiceHeader, InvoiceDetails, OtherCharges } from '../services/invoiceApi';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { userApiService } from '../services/userApi';
import { authApiService } from '../services/authApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { formatDate, formatDateForInput, formatDigitalSignedOnLine } from '../utils/dateUtils';
import { lineAmountFromQtyRate, resolveDisplayTotalTax, roundMoney2, roundRate2 } from '../utils/taxUtils';
import { InvoiceChunkedPrint } from './InvoiceChunkedPrint';
import {
  getInvoiceConfig,
  getInvoiceSupplyTypeCode,
  getRegularSupplyNote,
  isSupplyTypeWithoutTax,
  userHasEinvoiceCancelRole,
} from '../config/uiConfig';
import { getInvoiceExportCountryDisplayLine, isInvoiceExportSupplyType } from '../utils/invoiceExportCountry';
import { branchLutApiService, formatBranchLutForInvoiceDisplay } from '../services/branchLutApi';
import { commonApiService, PortOption } from '../services/commonApi';
import {
  transporterMasterApiService,
  TransporterMaster,
  getTransporterDisplayName,
} from '../services/transporterMasterApi';

// Simple number-to-words for INR (whole number only)
function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };

  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';

  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);

  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

// Backend may store tax rate as percentage (18) or basis points (1800 = 18%). Normalize for display.
function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

/** E-invoice API may return `{ message, details: { message } }` on HTTP 200 with success: false. */
function parseEinvoiceErrorPayload(res: unknown): { title: string; detail?: string } | null {
  if (res == null || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === true) return null;
  const summary = typeof o.message === 'string' ? o.message.trim() : '';
  let inner = '';
  const d = o.details;
  if (d != null && typeof d === 'object') {
    const m = (d as Record<string, unknown>).message;
    if (typeof m === 'string') inner = m.trim();
  }
  if (!inner && !summary) return { title: 'E-invoice generation failed.' };
  if (inner && summary && inner !== summary) {
    return { title: inner, detail: summary };
  }
  return { title: inner || summary || 'E-invoice generation failed.' };
}

/** E-way bill API may return `{ message, details: { message } }` on HTTP 200 with success: false. */
function parseEwayBillErrorPayload(res: unknown): { title: string; detail?: string } | null {
  if (res == null || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === true) return null;
  const summary = typeof o.message === 'string' ? o.message.trim() : '';
  let inner = '';
  const d = o.details;
  if (d != null && typeof d === 'object') {
    const m = (d as Record<string, unknown>).message;
    if (typeof m === 'string') inner = m.trim();
  }
  if (!inner && !summary) return { title: 'E-way bill generation failed.' };
  if (inner && summary && inner !== summary) {
    return { title: inner, detail: summary };
  }
  return { title: inner || summary || 'E-way bill generation failed.' };
}

const EINVOICE_CANCEL_REASONS = [
  { value: '1', label: 'Duplicate' },
  { value: '2', label: 'Data entry mistake' },
  { value: '3', label: 'Order Cancelled' },
  { value: '4', label: 'Others' },
] as const;

const EINVOICE_CANCEL_WINDOW_MS = 24 * 60 * 60 * 1000;

function getEinvoiceGeneratedAt(invoice: InvoiceHeader): Date | null {
  const inv = invoice as unknown as Record<string, unknown>;
  const raw = invoice.ackDate ?? invoice.signedOn ?? inv.signed_on;
  if (!raw) return null;
  const parsed = new Date(String(raw));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function canCancelEinvoiceWithin24Hours(invoice: InvoiceHeader | null, isEInvoiced: boolean): boolean {
  if (!invoice || !isEInvoiced) return false;
  if (!String(invoice.irn ?? '').trim()) return false;
  const generatedAt = getEinvoiceGeneratedAt(invoice);
  console.log('generatedAt', generatedAt);
  if (!generatedAt) return false;
  const elapsedMs = Date.now() - generatedAt.getTime();
  return elapsedMs >= 0 && elapsedMs < EINVOICE_CANCEL_WINDOW_MS;
}

const InvoiceViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState<InvoiceHeader | null>(null);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [shipToCustomer, setShipToCustomer] = useState<Customer | null>(null);
  const [billToCustomer, setBillToCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingEinvoice, setGeneratingEinvoice] = useState(false);
  const [generatingEwayBill, setGeneratingEwayBill] = useState(false);
  const [einvoiceError, setEinvoiceError] = useState<{ title: string; detail?: string } | null>(null);
  const [ewayBillError, setEwayBillError] = useState<{ title: string; detail?: string } | null>(null);
  const [isEwayDialogOpen, setIsEwayDialogOpen] = useState(false);
  const [isEwayEditMode, setIsEwayEditMode] = useState(false);
  const [ewayForm, setEwayForm] = useState<{
    ewayTransportMode: string;
    ewayTransporterId: string;
    ewayTransporterName: string;
    transporterMasterId: string;
    ewayDistanceKm: string;
    motorVehicleNo: string;
    portId: string;
    manualPincode: string;
  }>({
    ewayTransportMode: '',
    ewayTransporterId: '',
    ewayTransporterName: '',
    transporterMasterId: '',
    ewayDistanceKm: '',
    motorVehicleNo: '',
    portId: '',
    manualPincode: '',
  });
  const [ports, setPorts] = useState<PortOption[]>([]);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [transporters, setTransporters] = useState<TransporterMaster[]>([]);
  const [loadingTransporters, setLoadingTransporters] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [einvoicePassword, setEinvoicePassword] = useState('');
  const [einvoicePasswordError, setEinvoicePasswordError] = useState('');
  const [einvoiceEwayBillRequired, setEinvoiceEwayBillRequired] = useState(true);
  const [validatingPassword, setValidatingPassword] = useState(false);
  const [printCopies, setPrintCopies] = useState(1);
  const [einvoiceSignerName, setEinvoiceSignerName] = useState('');
  const [isCancelEinvoiceDialogOpen, setIsCancelEinvoiceDialogOpen] = useState(false);
  const [cancelEinvoiceReason, setCancelEinvoiceReason] = useState('');
  const [cancellingEinvoice, setCancellingEinvoice] = useState(false);
  const [cancelEinvoiceError, setCancelEinvoiceError] = useState<{ title: string; detail?: string } | null>(null);
  const [isCancelPasswordDialogOpen, setIsCancelPasswordDialogOpen] = useState(false);
  const [cancelEinvoicePassword, setCancelEinvoicePassword] = useState('');
  const [cancelEinvoicePasswordError, setCancelEinvoicePasswordError] = useState('');
  const [validatingCancelPassword, setValidatingCancelPassword] = useState(false);
  const [userRoleNames, setUserRoleNames] = useState<string[]>([]);
  const [userRolesLoaded, setUserRolesLoaded] = useState(false);
  const [exportBranchLutDisplay, setExportBranchLutDisplay] = useState('');
  const [exportBranchLutLoading, setExportBranchLutLoading] = useState(false);
  const originalDocumentTitleRef = useRef<string | null>(null);

  const reloadInvoice = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setInvoice(null);
      setBranchDetails(null);
      setShipToCustomer(null);
      setBillToCustomer(null);
      setEinvoiceError(null);
      setEwayBillError(null);

      const invRes = await invoiceApiService.getInvoiceHeaderById(id);
      if (!invRes.success || !invRes.data) {
        return;
      }
      const inv = invRes.data;
      const headerBranchId = selectedBranch != null ? Number(selectedBranch) : null;
      const invoiceBranchId = inv.branchId != null ? Number(inv.branchId) : null;
      if (
        headerBranchId == null ||
        invoiceBranchId == null ||
        headerBranchId !== invoiceBranchId
      ) {
        return;
      }

      const branchRes = await branchApiService.getBranchById(String(headerBranchId));
      setInvoice(inv);
      setBranchDetails(branchRes ?? null);

      const shipToId = inv.shipTo ?? inv.billTo;
      const billToId = inv.billTo;
      const [shipRes, billRes] = await Promise.all([
        shipToId ? customerApiService.getCustomerById(shipToId.toString()) : Promise.resolve({ success: false, data: null }),
        billToId ? customerApiService.getCustomerById(billToId.toString()) : Promise.resolve({ success: false, data: null })
      ]);
      if (shipRes.success && shipRes.data) setShipToCustomer(shipRes.data);
      if (billRes.success && billRes.data) setBillToCustomer(billRes.data);
    } catch (e) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id, selectedBranch]);

  useEffect(() => {
    reloadInvoice();
  }, [reloadInvoice]);

  useEffect(() => {
    if (!user?.id) {
      setUserRoleNames([]);
      setUserRolesLoaded(true);
      return;
    }
    let cancelled = false;
    setUserRolesLoaded(false);
    void (async () => {
      try {
        const names = await userApiService.getUserRoleNamesByUserId(user.id, true);
        if (cancelled) return;
        setUserRoleNames(names);
      } catch {
        if (!cancelled) setUserRoleNames([]);
      } finally {
        if (!cancelled) setUserRolesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!invoice) {
      setEinvoiceSignerName('');
      return;
    }
    const st = String(invoice.status ?? '')
      .toUpperCase()
      .replace(/-/g, '_');
    if (st !== 'E_INVOICED') {
      setEinvoiceSignerName('');
      return;
    }
    const fromApi = invoice.signedByName?.trim();
    if (fromApi) {
      setEinvoiceSignerName(fromApi);
      return;
    }
    const uid = invoice.signedBy ?? invoice.signed_by;
    if (uid == null || Number.isNaN(Number(uid))) {
      setEinvoiceSignerName('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await userApiService.getUserById(String(uid));
        if (cancelled || !res.success || !res.data) return;
        const u = res.data;
        const display =
          [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
          u.displayName ||
          u.fullName ||
          '';
        if (!cancelled) setEinvoiceSignerName(display);
      } catch {
        if (!cancelled) setEinvoiceSignerName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  const digitallySignedOnDisplay = useMemo(() => {
    if (!invoice) return '';
    return formatDigitalSignedOnLine(invoice as unknown as Record<string, unknown>);
  }, [invoice]);

  const exportSupply = invoice != null && isInvoiceExportSupplyType(invoice);
  const invBranchIdForLut = invoice?.branchId != null ? Number(invoice.branchId) : NaN;
  const invDateKeyForLut = invoice?.invoiceDate ? formatDateForInput(String(invoice.invoiceDate)) : '';

  useEffect(() => {
    if (!exportSupply || Number.isNaN(invBranchIdForLut) || invBranchIdForLut <= 0) {
      setExportBranchLutDisplay('');
      setExportBranchLutLoading(false);
      return;
    }
    let cancelled = false;
    setExportBranchLutLoading(true);
    void (async () => {
      try {
        const dto = await branchLutApiService.getEffectiveForBranchOnDate(
          invBranchIdForLut,
          invDateKeyForLut || undefined
        );
        if (cancelled) return;
        setExportBranchLutDisplay(dto ? formatBranchLutForInvoiceDisplay(dto) : '');
      } catch {
        if (!cancelled) setExportBranchLutDisplay('');
      } finally {
        if (!cancelled) setExportBranchLutLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportSupply, invBranchIdForLut, invDateKeyForLut]);

  useEffect(() => {
    if (!isEwayDialogOpen || !exportSupply) {
      return;
    }
    let cancelled = false;
    setLoadingPorts(true);
    void (async () => {
      try {
        const list = await commonApiService.getPorts();
        if (!cancelled) setPorts(list);
      } catch {
        if (!cancelled) setPorts([]);
      } finally {
        if (!cancelled) setLoadingPorts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEwayDialogOpen, exportSupply]);

  useEffect(() => {
    if (!isEwayDialogOpen) return;
    let cancelled = false;
    setLoadingTransporters(true);
    void (async () => {
      try {
        const res = await transporterMasterApiService.getAll();
        if (!cancelled && res.success && Array.isArray(res.data)) {
          setTransporters(res.data);
        } else if (!cancelled) {
          setTransporters([]);
        }
      } catch {
        if (!cancelled) setTransporters([]);
      } finally {
        if (!cancelled) setLoadingTransporters(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEwayDialogOpen]);

  useEffect(() => {
    if (!isEwayDialogOpen || transporters.length === 0) return;
    setEwayForm((p) => {
      if (p.transporterMasterId) return p;
      const gstin = p.ewayTransporterId.trim();
      const name = p.ewayTransporterName.trim();
      if (!gstin && !name) return p;
      const match = transporters.find(
        (t) =>
          (gstin && t.transporterId?.trim() === gstin) ||
          (name && t.transporterName?.trim() === name)
      );
      if (!match?.id) return p;
      return {
        ...p,
        transporterMasterId: String(match.id),
        ewayTransporterName: match.transporterName?.trim() ?? p.ewayTransporterName,
        ewayTransporterId: match.transporterId?.trim() ?? p.ewayTransporterId,
      };
    });
  }, [isEwayDialogOpen, transporters]);

  const handleEwayTransporterSelect = (masterId: string) => {
    if (!masterId || masterId === '__none__') {
      setEwayForm((p) => ({
        ...p,
        transporterMasterId: '',
        ewayTransporterName: '',
        ewayTransporterId: '',
      }));
      return;
    }
    const selected = transporters.find((t) => String(t.id) === masterId);
    setEwayForm((p) => ({
      ...p,
      transporterMasterId: masterId,
      ewayTransporterName: selected?.transporterName?.trim() ?? '',
      ewayTransporterId: selected?.transporterId?.trim() ?? '',
    }));
  };

  const handleGenerateEinvoice = async () => {
    if (!id) return;
    setEinvoiceError(null);
    try {
      setGeneratingEinvoice(true);
      const res = await invoiceApiService.generateEInvoice(id, einvoiceEwayBillRequired);
      if (res.success) {
        setEinvoiceError(null);
        toast.success('E-invoice generated successfully');
        await reloadInvoice();
      } else {
        const parsed = parseEinvoiceErrorPayload(res);
        if (parsed) setEinvoiceError(parsed);
        const toastText = parsed?.title ?? (res as { message?: string }).message ?? 'Failed to generate e-invoice';
        toast.error(toastText);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate e-invoice';
      setEinvoiceError({ title: msg });
      toast.error(msg);
    } finally {
      setGeneratingEinvoice(false);
    }
  };

  const handleCancelEinvoice = async () => {
    if (!id || !invoice) return;
    const irn = String(invoice.irn ?? '').trim();
    if (!irn) {
      toast.error('IRN is missing on this invoice.');
      return;
    }
    if (!cancelEinvoiceReason) {
      toast.error('Please select a reason for cancellation.');
      return;
    }
    setCancelEinvoiceError(null);
    try {
      setCancellingEinvoice(true);
      const selectedReason = EINVOICE_CANCEL_REASONS.find((r) => r.value === cancelEinvoiceReason);
      const res = await invoiceApiService.cancelEInvoice(id, {
        irn,
        cnlrsn: cancelEinvoiceReason,
        cnlrem: selectedReason?.label ?? '',
      });
      if (res.success) {
        toast.success('E-invoice cancelled successfully');
        setIsCancelEinvoiceDialogOpen(false);
        setCancelEinvoiceReason('');
        await reloadInvoice();
      } else {
        const parsed = parseEinvoiceErrorPayload(res);
        if (parsed) setCancelEinvoiceError(parsed);
        toast.error(parsed?.title ?? (res as { message?: string }).message ?? 'Failed to cancel e-invoice');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to cancel e-invoice';
      setCancelEinvoiceError({ title: msg });
      toast.error(msg);
    } finally {
      setCancellingEinvoice(false);
    }
  };

  const openCancelPasswordDialog = () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    if (!cancelEinvoiceReason) {
      toast.error('Please select a reason for cancellation.');
      return;
    }
    setCancelEinvoicePassword('');
    setCancelEinvoicePasswordError('');
    setIsCancelPasswordDialogOpen(true);
  };

  const handleCancelPasswordVerification = async () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    if (!cancelEinvoicePassword.trim()) {
      setCancelEinvoicePasswordError('Please enter your password.');
      return;
    }
    try {
      setCancelEinvoicePasswordError('');
      setValidatingCancelPassword(true);
      await authApiService.authenticateUser(user.username, cancelEinvoicePassword);
      setIsCancelPasswordDialogOpen(false);
      setCancelEinvoicePassword('');
      setCancelEinvoicePasswordError('');
      await handleCancelEinvoice();
    } catch {
      setCancelEinvoicePasswordError('Invalid password.');
    } finally {
      setValidatingCancelPassword(false);
    }
  };

  const handleGenerateEWayBill = async () => {
    if (!id) return;
    setEwayBillError(null);
    const irn = String(invoice?.irn ?? '').trim();
    if (!irn) {
      toast.error('IRN is required before generating an e-way bill.');
      return;
    }
    const isExport = invoice != null && isInvoiceExportSupplyType(invoice);
    if (isExport) {
      const portIdNum = parseInt(ewayForm.portId, 10);
      if (!ewayForm.portId.trim() || Number.isNaN(portIdNum) || portIdNum <= 0) {
        toast.error('Please select a port for export e-way bill.');
        setIsEwayDialogOpen(true);
        return;
      }
      const manualPin = ewayForm.manualPincode.trim();
      if (!/^\d{6}$/.test(manualPin)) {
        toast.error('Please enter a valid 6-digit manual pincode for export e-way bill.');
        setIsEwayDialogOpen(true);
        return;
      }
    }
    try {
      setGeneratingEwayBill(true);

      const distanceRaw = String(ewayForm.ewayDistanceKm ?? '').trim();
      const distanceNum = distanceRaw ? Number(distanceRaw) : undefined;
      const portIdNum = parseInt(ewayForm.portId, 10);
      const transporterMasterIdNum = parseInt(ewayForm.transporterMasterId, 10);
      const updatePayload = {
        id: Number(id),
        ewayTransportMode: String(ewayForm.ewayTransportMode ?? '').trim() || undefined,
        ewayTransporterId: String(ewayForm.ewayTransporterId ?? '').trim() || undefined,
        ewayTransporterName: String(ewayForm.ewayTransporterName ?? '').trim() || undefined,
        ewayDistanceKm: distanceNum != null && !Number.isNaN(distanceNum) ? distanceNum : undefined,
        motorVehicleNo: String(ewayForm.motorVehicleNo ?? '').trim() || undefined,
        // Backward-compatible keys (some backends accept snake_case)
        eway_transporter_id: String(ewayForm.ewayTransporterId ?? '').trim() || undefined,
        eway_transporter_name: String(ewayForm.ewayTransporterName ?? '').trim() || undefined,
        ...(ewayForm.transporterMasterId &&
        !Number.isNaN(transporterMasterIdNum) &&
        transporterMasterIdNum > 0
          ? { transporterMasterId: transporterMasterIdNum }
          : {}),
        ...(isExport && !Number.isNaN(portIdNum) && portIdNum > 0 ? { portId: portIdNum } : {}),
        ...(isExport && ewayForm.manualPincode.trim()
          ? { manualPincode: ewayForm.manualPincode.trim() }
          : {}),
      };
      const updRes = await invoiceApiService.updateInvoiceHeader(id, updatePayload);
      if (!updRes.success) {
        toast.error((updRes as { message?: string }).message ?? 'Failed to save transport details');
        return;
      }

      const res = await invoiceApiService.generateEWayBill(id);
      if (res.success && res.data) {
        setInvoice(res.data);
        setEwayBillError(null);
        toast.success('E-way bill generated successfully');
      } else if (res.success) {
        await reloadInvoice();
        setEwayBillError(null);
        toast.success('E-way bill generated successfully');
      } else {
        const parsed = parseEwayBillErrorPayload(res);
        if (parsed) setEwayBillError(parsed);
        const toastText =
          parsed?.title ?? (res as { message?: string }).message ?? 'Failed to generate e-way bill';
        toast.error(toastText);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate e-way bill';
      setEwayBillError({ title: msg });
      toast.error(msg);
    } finally {
      setGeneratingEwayBill(false);
    }
  };

  const openEwayDialog = () => {
    const inv = invoice as Record<string, unknown> | null;
    const ewayTransporterId = String(inv?.ewayTransporterId ?? inv?.eway_transporter_id ?? '').trim();
    const ewayTransporterName = String(inv?.ewayTransporterName ?? inv?.eway_transporter_name ?? '').trim();
    let transporterMasterId =
      inv?.transporterMasterId != null ? String(inv.transporterMasterId).trim() : '';
    setEwayForm({
      ewayTransportMode: String(inv?.ewayTransportMode ?? '').trim(),
      ewayTransporterId,
      ewayTransporterName,
      transporterMasterId,
      ewayDistanceKm:
        inv?.ewayDistanceKm != null && String(inv.ewayDistanceKm).trim() !== ''
          ? String(inv.ewayDistanceKm)
          : '',
      motorVehicleNo: String(inv?.motorVehicleNo ?? '').trim(),
      portId: String(inv?.portId ?? inv?.port_id ?? '').trim(),
      manualPincode: String(inv?.manualPincode ?? inv?.manual_pincode ?? '').trim(),
    });
    setIsEwayEditMode(false);
    setIsEwayDialogOpen(true);
  };

  const getEinvoiceValidationError = (ewayBillRequired: boolean): string | null => {
    const customer = billToCustomer;
    const supplierStateCode = String(branchDetails?.stateCode ?? '').trim();
    const customerStateCode = String(customer?.stateCode ?? '').trim();
    const isExport = invoice != null && isInvoiceExportSupplyType(invoice);

    if (!customerStateCode) return 'Customer state code is missing. Please update customer master.';
    const gstNo = String(customer?.gstNo ?? '').trim();
    if (!isExport && !gstNo) return 'Customer GST number is missing. Please update customer master.';

    const address = String(customer?.address ?? '').trim();
    const hasPincode = /\b\d{6}\b/.test(address);
    if (!isExport && !hasPincode) {
      return 'Customer pincode is missing in address. Please update customer address.';
    }

    const details = invoice?.invoiceDetails ?? [];
    const badUomLine = details.find((d) => {
      const uom = String((d as any)?.uom ?? '').trim();
      return !uom || uom.length < 3;
    });
    if (badUomLine) {
      const name = String((badUomLine as any)?.endgoodName ?? '').trim() || 'an item';
      return `Item UOM is missing/invalid for ${name}. UOM must be at least 3 characters.`;
    }

    if (ewayBillRequired) {
      const vehicleNo = String(invoice?.motorVehicleNo ?? '').trim();
      if (!vehicleNo) return 'Vehicle number is required. Please enter Motor Vehicle No. in invoice.';

      const isInterState = supplierStateCode && customerStateCode && supplierStateCode !== customerStateCode;
      if (isInterState) {
        const transporterMasterId = (invoice as any)?.transporterMasterId;
        const hasTransporterMaster =
          transporterMasterId != null && !Number.isNaN(Number(transporterMasterId)) && Number(transporterMasterId) > 0;
        const ewayTransporterId = String((invoice as any)?.ewayTransporterId ?? (invoice as any)?.eway_transporter_id ?? '').trim();
        if (!hasTransporterMaster && !ewayTransporterId) {
          return 'Transporter is required for inter-state supply. Please select transporter.';
        }
      }
    }

    return null;
  };

  const openEinvoicePasswordDialog = () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    const err = getEinvoiceValidationError(false);
    if (err) {
      toast.error(err);
      setEinvoiceError({ title: err });
      return;
    }
    setEinvoicePassword('');
    setEinvoicePasswordError('');
    setEinvoiceEwayBillRequired(true);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordVerificationAndGenerate = async () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    const validationError = getEinvoiceValidationError(einvoiceEwayBillRequired);
    if (validationError) {
      toast.error(validationError);
      setEinvoiceError({ title: validationError });
      return;
    }
    if (!einvoicePassword.trim()) {
      setEinvoicePasswordError('Please enter your password.');
      return;
    }

    try {
      setEinvoicePasswordError('');
      setValidatingPassword(true);
      await authApiService.authenticateUser(user.username, einvoicePassword);
      setIsPasswordDialogOpen(false);
      setEinvoicePassword('');
      setEinvoicePasswordError('');
      await handleGenerateEinvoice();
    } catch {
      setEinvoicePasswordError('Invalid password.');
    } finally {
      setValidatingPassword(false);
    }
  };

  const handlePrintWithCopies = async () => {
    const input = window.prompt('How many copies do you want to print?', String(printCopies || 1));
    if (input == null) return;
    const n = Math.max(1, Math.min(20, parseInt(input, 10) || 1));
    setPrintCopies(n);
    if (invoice?.invoiceNo) {
      if (originalDocumentTitleRef.current == null) originalDocumentTitleRef.current = document.title;
      document.title = invoice.invoiceNo;
    }
    // Allow state to apply before opening print dialog
    setTimeout(() => window.print(), 50);
  };

  const statusNorm = String(invoice?.status ?? '')
    .toUpperCase()
    .replace(/-/g, '_');
  const isEInvoiced = statusNorm === 'E_INVOICED';
  const isEInvoicedCancelled = statusNorm === 'E_INVOICED_CANCELLED';
  const invoiceConfig = getInvoiceConfig();
  const enabledEinvoiceBranchCodes = invoiceConfig.einvoiceEnabledBranchCodes.map((code) =>
    String(code).toUpperCase()
  );
  const isEinvoiceEnabledForBranch =
    enabledEinvoiceBranchCodes.length === 0 ||
    enabledEinvoiceBranchCodes.includes(String(branchDetails?.branchCode ?? '').toUpperCase());
  const canCancelEinvoice =
    userRolesLoaded &&
    userHasEinvoiceCancelRole(userRoleNames) &&
    isEinvoiceEnabledForBranch &&
    canCancelEinvoiceWithin24Hours(invoice, isEInvoiced);

  // Default to 4 copies for e-invoice print
  useEffect(() => {
    setPrintCopies(isEInvoiced ? 4 : 1);
  }, [isEInvoiced]);

  useEffect(() => {
    const prev = window.onafterprint;
    window.onafterprint = () => {
      setPrintCopies(isEInvoiced ? 4 : 1);
      if (originalDocumentTitleRef.current != null) {
        document.title = originalDocumentTitleRef.current;
        originalDocumentTitleRef.current = null;
      }
      if (typeof prev === 'function') prev.call(window, new Event('afterprint'));
    };
    return () => {
      window.onafterprint = prev ?? null;
    };
  }, [isEInvoiced]);

  const isGst = useMemo(() => {
    if (!branchDetails || !billToCustomer) return true;
    return (branchDetails.stateCode ?? '') !== (billToCustomer.stateCode ?? '');
  }, [branchDetails, billToCustomer]);

  const calculationDetails = useMemo(() => {
    if (!invoice?.invoiceDetails) return null;
    const details = invoice.invoiceDetails;
    const taxableAmount = details.reduce((s, d) => s + lineAmountFromQtyRate(d.qty ?? 0, d.rate ?? 0), 0);
    const headerTax =
      invoice.grandTotalTax != null && !Number.isNaN(Number(invoice.grandTotalTax))
        ? Number(invoice.grandTotalTax)
        : 0;
    const totalCgst = details.reduce((s, d) => s + (d.cgst ?? 0), 0);
    const totalSgst = details.reduce((s, d) => s + (d.sgst ?? 0), 0);
    const totalIgst = details.reduce((s, d) => s + (d.igst ?? 0), 0);
    const computedTotalTax = totalCgst + totalSgst + totalIgst;
    const otherChargesTotal = (invoice.otherCharges ?? []).reduce((s, c) => s + (c.amount || 0), 0);
    const totalTax = resolveDisplayTotalTax(computedTotalTax, headerTax);
    const totalAmount = roundMoney2(taxableAmount + totalTax + otherChargesTotal);
    const totalBoxQty = details.reduce((s, d) => {
      const boxes = d.boxes ? parseInt(String(d.boxes).replace(/\D/g, ''), 10) : 0;
      return s + (isNaN(boxes) ? 0 : boxes);
    }, 0);
    const totalQty = details.reduce((s, d) => s + (d.qty ?? 0), 0);
    return {
      taxableAmount,
      totalCgst,
      totalSgst,
      totalIgst,
      totalTax,
      otherChargesTotal,
      totalAmount,
      totalBoxQty,
      totalQty,
      totalTaxableAmount: totalTax
    };
  }, [invoice]);

  const hsnWiseTaxDetails = useMemo((): HsnWiseRow[] => {
    if (!invoice?.invoiceDetails) return [];
    const details = invoice.invoiceDetails.filter(
      (d) => lineAmountFromQtyRate(d.qty ?? 0, d.rate ?? 0) > 0
    );
    const supplyType = String(
      (invoice as { supTyp?: string; sup_typ?: string; supplyType?: string }).supTyp ??
        (invoice as { sup_typ?: string }).sup_typ ??
        (invoice as { supplyType?: string }).supplyType ??
        ''
    ).trim();
    const supplyWithoutTax = isSupplyTypeWithoutTax(supplyType);

    const byHsn: Record<
      string,
      {
        taxableValue: number;
        cgstAmount: number;
        sgstAmount: number;
        igstAmount: number;
        cgstRate: number;
        sgstRate: number;
        igstRate: number;
      }
    > = {};

    details.forEach((d) => {
      const hsn = d.hsn || 'N/A';
      const amt = lineAmountFromQtyRate(d.qty ?? 0, d.rate ?? 0);
      if (!byHsn[hsn]) {
        byHsn[hsn] = {
          taxableValue: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          cgstRate: toDisplayRate(d.cgst ?? 0),
          sgstRate: toDisplayRate(d.sgst ?? 0),
          igstRate: toDisplayRate(d.igst ?? 0),
        };
      }
      byHsn[hsn].taxableValue += amt;
      byHsn[hsn].cgstAmount += d.cgst ?? 0;
      byHsn[hsn].sgstAmount += d.sgst ?? 0;
      byHsn[hsn].igstAmount += d.igst ?? 0;
    });

    return Object.entries(byHsn).map(([hsn, v]) => {
      const tv = v.taxableValue;
      const deriveDisplayRate = (amount: number, fallbackRate: number) => {
        if (supplyWithoutTax) return 0;
        if (tv > 0 && amount > 0) {
          return roundMoney2((amount / tv) * 100);
        }
        return toDisplayRate(fallbackRate);
      };
      const cgstRate = deriveDisplayRate(v.cgstAmount, v.cgstRate);
      const sgstRate = deriveDisplayRate(v.sgstAmount, v.sgstRate);
      const igstRate = deriveDisplayRate(v.igstAmount, v.igstRate);
      return {
        hsn,
        taxableValue: tv,
        cgstRate,
        cgstAmount: v.cgstAmount,
        sgstRate,
        sgstAmount: v.sgstAmount,
        igstRate,
        igstAmount: v.igstAmount,
        totalTaxAmount: v.cgstAmount + v.sgstAmount + v.igstAmount,
      };
    });
  }, [invoice]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
        <Card className="border-primary-200 shadow-md">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Invoice not found</p>
            <p className="text-sm text-gray-500 mt-2">
              This invoice does not exist or is not available for the selected branch.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const details = invoice.invoiceDetails ?? [];
  const otherChargesList = invoice.otherCharges ?? [];
  const calc = calculationDetails!;
  const supplyNote = getRegularSupplyNote(getInvoiceSupplyTypeCode(invoice));
  // statusNorm / isEInvoiced computed above
  const hasEwayBillNo = invoice.ewayBillNo != null && String(invoice.ewayBillNo).trim() !== '';
  const hasIrn = String(invoice.irn ?? '').trim() !== '';
  const showEwayBillOnView = isEInvoiced && hasEwayBillNo;
  const canOfferGenerateEwayBill = isEInvoiced && !hasEwayBillNo;
  const qrValueRaw =
    (invoice as any).signedQrcode ||
    (invoice as any).signedQRcode ||
    (invoice as any).signedQRCode ||
    (invoice as any).signedQrCode ||
    (invoice as any).qrCode ||
    (invoice as any).qrString ||
    '';
  const qrValue = String(qrValueRaw ?? '')
    .trim()
    .replace(/^"(.*)"$/, '$1');

  const copies = isEInvoiced ? Math.max(1, printCopies) : 1;

  return (
    <div className="container mx-auto p-6 space-y-4">
      {einvoiceError && (
        <Alert variant="destructive" className="print:hidden relative pr-10">
          <AlertCircle className="size-4" />
          <AlertTitle className="line-clamp-none whitespace-normal">E-invoice could not be generated</AlertTitle>
          <AlertDescription className="text-left col-start-2">
            <p className="font-medium text-destructive">{einvoiceError.title}</p>
            {einvoiceError.detail ? (
              <p className="mt-2 text-sm opacity-90">{einvoiceError.detail}</p>
            ) : null}
          </AlertDescription>
          <button
            type="button"
            onClick={() => setEinvoiceError(null)}
            className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      {ewayBillError && (
        <Alert variant="destructive" className="print:hidden relative pr-10">
          <AlertCircle className="size-4" />
          <AlertTitle className="line-clamp-none whitespace-normal">E-way bill could not be generated</AlertTitle>
          <AlertDescription className="text-left col-start-2">
            <p className="font-medium text-destructive">{ewayBillError.title}</p>
            {ewayBillError.detail ? (
              <p className="mt-2 text-sm opacity-90">{ewayBillError.detail}</p>
            ) : null}
          </AlertDescription>
          <button
            type="button"
            onClick={() => setEwayBillError(null)}
            className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* Header Actions - Hidden when printing */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
        {isEInvoicedCancelled ? (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-red-800">
            <Ban className="h-4 w-4 shrink-0" />
            <span className="font-medium">E-invoice is cancelled.</span>
          </div>
        ) : isEInvoiced ? (
          <div className="flex flex-wrap items-center gap-2">
            {canCancelEinvoice ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCancelEinvoiceError(null);
                  setIsCancelEinvoiceDialogOpen(true);
                }}
                disabled={cancellingEinvoice}
                className="border-red-200 text-red-700 hover:bg-red-50"
                title="Cancel e-invoice IRN (allowed within 24 hours of generation)"
              >
                {cancellingEinvoice ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4 mr-2" />
                )}
                Cancel e-invoice
              </Button>
            ) : null}
            {canOfferGenerateEwayBill ? (
              <Button
                type="button"
                variant="outline"
                onClick={openEwayDialog}
                disabled={generatingEwayBill || !hasIrn}
                title={
                  !hasIrn
                    ? 'IRN is required before generating an e-way bill.'
                    : 'Generate e-way bill (document) via GST portal'
                }
                className="border-primary-200 text-primary-800 hover:bg-primary-50"
              >
                {generatingEwayBill ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Truck className="h-4 w-4 mr-2" />
                )}
                {generatingEwayBill ? 'Generating…' : 'Generate e-way bill'}
              </Button>
            ) : null}
            <Button onClick={handlePrintWithCopies} className="bg-primary-600 hover:bg-primary-700 text-white">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)}>
              Edit Invoice
            </Button>
            {isEinvoiceEnabledForBranch && (
              <Button
                onClick={openEinvoicePasswordDialog}
                disabled={generatingEinvoice || validatingPassword}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                {generatingEinvoice ? 'Generating...' : 'Generate e-invoice'}
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={isEwayDialogOpen}
        onOpenChange={(open) => {
          if (generatingEwayBill) return;
          setIsEwayDialogOpen(open);
          if (!open) setIsEwayEditMode(false);
        }}
      >
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Transport details for e-way bill</DialogTitle>
            <DialogDescription>
              Review the transport details below. Enable edit if you want to update them before generating the e-way bill.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Invoice: <span className="font-medium text-foreground">{invoice?.invoiceNo ?? ''}</span>
            </div>
            <Button type="button" variant="outline" onClick={() => setIsEwayEditMode((v) => !v)}>
              {isEwayEditMode ? 'View mode' : 'Edit'}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ewayTransportMode">Transport mode</Label>
              <Input
                id="ewayTransportMode"
                value={ewayForm.ewayTransportMode}
                onChange={(e) => setEwayForm((p) => ({ ...p, ewayTransportMode: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="Road / Air / Rail / Ship"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="motorVehicleNo">Vehicle no.</Label>
              <Input
                id="motorVehicleNo"
                value={ewayForm.motorVehicleNo}
                onChange={(e) => setEwayForm((p) => ({ ...p, motorVehicleNo: e.target.value }))}
                disabled={!isEwayEditMode}
                placeholder="e.g. MH12AB1234"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label>Transporter</Label>
              {isEwayEditMode ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5 min-w-0">
                    <Label htmlFor="ewayTransporterMasterId" className="text-xs text-muted-foreground font-normal">
                      Name
                    </Label>
                    <Select
                      value={ewayForm.transporterMasterId || '__none__'}
                      onValueChange={handleEwayTransporterSelect}
                      disabled={loadingTransporters}
                    >
                      <SelectTrigger
                        id="ewayTransporterMasterId"
                        className="border border-gray-300 rounded-xl min-w-0 w-full [&>span]:block [&>span]:truncate [&>span]:text-left"
                      >
                        <SelectValue
                          placeholder={loadingTransporters ? 'Loading transporters…' : 'Select transporter'}
                        />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search transporter..." debounceMs={200}>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {[...transporters]
                          .sort((a, b) =>
                            getTransporterDisplayName(a).localeCompare(getTransporterDisplayName(b), undefined, {
                              sensitivity: 'base',
                            })
                          )
                          .map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {getTransporterDisplayName(t)}
                              {t.transporterId ? ` (${t.transporterId})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContentWithSearch>
                    </Select>
                  </div>
                  <div className="space-y-1.5 min-w-0">
                    <Label htmlFor="ewayTransporterId" className="text-xs text-muted-foreground font-normal">
                      ID
                    </Label>
                    <Input
                      id="ewayTransporterId"
                      value={ewayForm.ewayTransporterId}
                      readOnly
                      disabled
                      placeholder="Auto-filled from transporter"
                      className="bg-muted/40"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-foreground py-2 px-3 rounded-md border border-gray-200 bg-muted/30 break-words">
                  <span className="font-medium">{ewayForm.ewayTransporterName || '—'}</span>
                  {ewayForm.ewayTransporterId ? (
                    <span className="text-muted-foreground">
                      {' '}
                      · <span className="font-normal">ID:</span> {ewayForm.ewayTransporterId}
                    </span>
                  ) : null}
                </p>
              )}
            </div>

            {exportSupply ? (
              <>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="ewayPortId">Port *</Label>
                  <Select
                    value={ewayForm.portId || undefined}
                    onValueChange={(value) => setEwayForm((p) => ({ ...p, portId: value }))}
                    disabled={!isEwayEditMode || loadingPorts}
                  >
                    <SelectTrigger id="ewayPortId" className="border border-gray-300 rounded-xl w-full">
                      <SelectValue
                        placeholder={loadingPorts ? 'Loading ports…' : 'Select port'}
                      />
                    </SelectTrigger>
                    <SelectContentWithSearch
                      searchPlaceholder="Search port..."
                      className="min-w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,32rem)]"
                    >
                      {ports.map((p) => (
                        <SelectItem key={p.value} value={p.value} textValue={p.label}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                </div>
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ewayDistanceKm">Distance (KM)</Label>
                    <Input
                      id="ewayDistanceKm"
                      value={ewayForm.ewayDistanceKm}
                      onChange={(e) => setEwayForm((p) => ({ ...p, ewayDistanceKm: e.target.value }))}
                      disabled={!isEwayEditMode}
                      inputMode="numeric"
                      placeholder="e.g. 120"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ewayManualPincode">Manual pincode *</Label>
                    <Input
                      id="ewayManualPincode"
                      value={ewayForm.manualPincode}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setEwayForm((p) => ({ ...p, manualPincode: v }));
                      }}
                      disabled={!isEwayEditMode}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="6-digit pincode"
                    />
                    <p className="text-xs text-muted-foreground">
                      Required for export when buyer address has no Indian pincode.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="ewayDistanceKm">Distance (KM)</Label>
                <Input
                  id="ewayDistanceKm"
                  value={ewayForm.ewayDistanceKm}
                  onChange={(e) => setEwayForm((p) => ({ ...p, ewayDistanceKm: e.target.value }))}
                  disabled={!isEwayEditMode}
                  inputMode="numeric"
                  placeholder="e.g. 120"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setIsEwayDialogOpen(false)} disabled={generatingEwayBill}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (exportSupply) {
                  const portIdNum = parseInt(ewayForm.portId, 10);
                  if (!ewayForm.portId.trim() || Number.isNaN(portIdNum) || portIdNum <= 0) {
                    toast.error('Please select a port for export e-way bill.');
                    return;
                  }
                  if (!/^\d{6}$/.test(ewayForm.manualPincode.trim())) {
                    toast.error('Please enter a valid 6-digit manual pincode for export e-way bill.');
                    return;
                  }
                }
                setIsEwayDialogOpen(false);
                await handleGenerateEWayBill();
              }}
              disabled={generatingEwayBill}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {generatingEwayBill ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Truck className="h-4 w-4 mr-2" />
                  Save & generate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancelEinvoiceDialogOpen}
        onOpenChange={(open) => {
          if (cancellingEinvoice) return;
          setIsCancelEinvoiceDialogOpen(open);
          if (!open) {
            setCancelEinvoiceReason('');
            setCancelEinvoiceError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cancel e-invoice</DialogTitle>
            <DialogDescription>
              Cancel the IRN via GST portal. This is allowed only within 24 hours of e-invoice generation.
            </DialogDescription>
          </DialogHeader>
          {cancelEinvoiceError ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{cancelEinvoiceError.title}</AlertTitle>
              {cancelEinvoiceError.detail ? (
                <AlertDescription>{cancelEinvoiceError.detail}</AlertDescription>
              ) : null}
            </Alert>
          ) : null}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-einvoice-invoice-no">Invoice No</Label>
              <Input
                id="cancel-einvoice-invoice-no"
                value={String(invoice?.invoiceNo ?? '')}
                readOnly
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cancel-einvoice-irn">IRN</Label>
              <Input
                id="cancel-einvoice-irn"
                value={String(invoice?.irn ?? '')}
                readOnly
                className="bg-gray-50 font-mono text-xs break-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cancel-einvoice-reason">Reason for cancellation</Label>
              <Select
                value={cancelEinvoiceReason || undefined}
                onValueChange={setCancelEinvoiceReason}
                disabled={cancellingEinvoice}
              >
                <SelectTrigger id="cancel-einvoice-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {EINVOICE_CANCEL_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.value} — {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="!flex-row justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCancelEinvoiceDialogOpen(false)}
              disabled={cancellingEinvoice}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={openCancelPasswordDialog}
              disabled={cancellingEinvoice || validatingCancelPassword || !cancelEinvoiceReason}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {cancellingEinvoice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling…
                </>
              ) : (
                'Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancelPasswordDialogOpen}
        onOpenChange={(open) => {
          if (validatingCancelPassword || cancellingEinvoice) return;
          setIsCancelPasswordDialogOpen(open);
          if (!open) {
            setCancelEinvoicePassword('');
            setCancelEinvoicePasswordError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Enter your password to cancel the e-invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-einvoice-password">Password</Label>
            <Input
              id="cancel-einvoice-password"
              type="password"
              value={cancelEinvoicePassword}
              onChange={(e) => {
                setCancelEinvoicePassword(e.target.value);
                if (cancelEinvoicePasswordError) setCancelEinvoicePasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCancelPasswordVerification();
                }
              }}
              placeholder="Enter your password"
              disabled={validatingCancelPassword || cancellingEinvoice}
              className={cancelEinvoicePasswordError ? 'border-red-500 focus-visible:ring-red-500' : undefined}
            />
            {cancelEinvoicePasswordError ? (
              <p className="text-sm text-red-600">{cancelEinvoicePasswordError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCancelPasswordDialogOpen(false);
                setCancelEinvoicePassword('');
                setCancelEinvoicePasswordError('');
              }}
              disabled={validatingCancelPassword || cancellingEinvoice}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => void handleCancelPasswordVerification()}
              disabled={validatingCancelPassword || cancellingEinvoice}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {validatingCancelPassword || cancellingEinvoice ? 'Verifying...' : 'Confirm cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={(open) => {
          if (validatingPassword) return;
          setIsPasswordDialogOpen(open);
          if (!open) {
            setEinvoicePassword('');
            setEinvoicePasswordError('');
            setEinvoiceEwayBillRequired(true);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Enter your password to generate the e-invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
            <Checkbox
              id="einvoice-eway-bill-required"
              checked={einvoiceEwayBillRequired}
              onCheckedChange={(checked) => setEinvoiceEwayBillRequired(checked === true)}
              disabled={validatingPassword}
            />
            <div className="space-y-0.5">
              <Label htmlFor="einvoice-eway-bill-required" className="cursor-pointer font-medium leading-snug">
                Generate e-way bill with this invoice
              </Label>
              <p className="text-xs text-gray-600">
                When enabled, the e-way bill is requested from GSP along with the e-invoice (if transport details allow).
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="einvoice-password">Password</Label>
            <Input
              id="einvoice-password"
              type="password"
              value={einvoicePassword}
              onChange={(e) => {
                setEinvoicePassword(e.target.value);
                if (einvoicePasswordError) setEinvoicePasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handlePasswordVerificationAndGenerate();
                }
              }}
              placeholder="Enter your password"
              disabled={validatingPassword}
              className={einvoicePasswordError ? 'border-red-500 focus-visible:ring-red-500' : undefined}
            />
            {einvoicePasswordError ? (
              <p className="text-sm text-red-600">{einvoicePasswordError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsPasswordDialogOpen(false);
                setEinvoicePassword('');
                setEinvoicePasswordError('');
              }}
              disabled={validatingPassword}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handlePasswordVerificationAndGenerate()}
              disabled={validatingPassword}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {validatingPassword ? 'Verifying...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isEInvoiced && (
      <div>
      {Array.from({ length: copies }).map((_, copyIndex) => {
        return (
          <React.Fragment key={copyIndex}>
            <div className={`${copyIndex === 0 ? '' : 'hidden print:block'}`}>
              <div className="invoice-container border rounded-lg p-6 bg-white shadow-sm print:shadow-none print:border print:border-gray-300">
                <div className="flex flex-col items-center gap-1 mt-2 mb-2">
                  <h2 className="text-center font-bold text-xl">Tax Invoice</h2>
                  {supplyNote ? (
                    <p className="text-center text-[10px] leading-snug text-gray-800 max-w-3xl px-2 font-medium print:text-[10px]">
                      {supplyNote}
                    </p>
                  ) : null}
                </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 mb-6 min-w-0">
          {/* Left: Seller, Consignee, Buyer */}
          <div className="space-y-4 border rounded p-4 min-w-0">
            <div className="border-b pb-3">
              <div className="flex gap-4">
                {branchDetails && (
                  <>
                    <div className="flex-shrink-0">
                      <img src="/customer-logo/sg-group-logo.jpg" alt="SG Group" className="h-14 w-20 object-contain" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{branchDetails.branchName}</p>
                      <p className="text-xs text-gray-600 leading-snug">{branchDetails.address}</p>
                      <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                      <p className="text-xs text-gray-600 leading-snug">State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="border-b pb-3">
              <p className="font-semibold mb-2">Consignee (Ship to):</p>
              {shipToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{shipToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{shipToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
            <div>
              <p className="font-semibold mb-2">Buyer (Bill to):</p>
              {billToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{billToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{billToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
          </div>

          {/* Right: Invoice particulars — one line per field (Label: value) */}
          <div className="invoice-meta-lines min-w-0 border border-gray-300 rounded overflow-hidden text-[11px] leading-snug divide-y divide-gray-300">
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Invoice No.: </span>
              <span className="text-gray-800">{invoice.invoiceNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dated: </span>
              <span className="text-gray-800">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">e-Way Bill No.: </span>
              <span className="text-gray-800">{invoice.ewayBillNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Delivery Note: </span>
              <span className="text-gray-800">{invoice.deliverNote ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Reference No. &amp; Date: </span>
              <span className="text-gray-800">{invoice.referenceNoAndDate ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Other References: </span>
              <span className="text-gray-800">{invoice.otherReferences ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Buyer&apos;s Order No.: </span>
              <span className="text-gray-800">{invoice.buyerOrderNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Buyer order dated: </span>
              <span className="text-gray-800">{invoice.buyerOrderDate ? formatDate(invoice.buyerOrderDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dispatch Doc No.: </span>
              <span className="text-gray-800">{invoice.dispatchDocNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Delivery Note Date: </span>
              <span className="text-gray-800">{invoice.deliverNoteDate ? formatDate(invoice.deliverNoteDate) : ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Dispatched through: </span>
              <span className="text-gray-800">{invoice.dispatchedThrough ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Destination: </span>
              <span className="text-gray-800">{invoice.destination ?? ''}</span>
            </div>
            {isInvoiceExportSupplyType(invoice) ? (
              <div className="px-2 py-1.5 break-words bg-white">
                <span className="font-semibold text-gray-900">Country: </span>
                <span className="text-gray-800">{getInvoiceExportCountryDisplayLine(invoice)}</span>
              </div>
            ) : null}
            {isInvoiceExportSupplyType(invoice) ? (
              <div className="px-2 py-1.5 break-words bg-white">
                <span className="font-semibold text-gray-900">LUT: </span>
                <span className="text-gray-800">
                  {exportBranchLutLoading ? '…' : exportBranchLutDisplay || '—'}
                </span>
              </div>
            ) : null}
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Bill of Lading / LR-RR No.: </span>
              <span className="text-gray-800">{invoice.billOfLanding ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Motor Vehicle No.: </span>
              <span className="text-gray-800">{invoice.motorVehicleNo ?? ''}</span>
            </div>
            <div className="px-2 py-1.5 break-words bg-white">
              <span className="font-semibold text-gray-900">Term of delivery: </span>
              <span className="text-gray-800">{invoice.termOfDelivery ?? ''}</span>
            </div>
          </div>
        </div>

        {/* Itemized table - only show when there are line items */}
        {details.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-center text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-2">#</th>
                <th className="border border-gray-300 px-2 py-2">Description of Goods</th>
                <th className="border border-gray-300 px-2 py-2">Boxes</th>
                <th className="border border-gray-300 px-2 py-2 min-w-[120px]">Heat No/Container</th>
                <th className="border border-gray-300 px-2 py-2">HSN/SAC</th>
                <th className="border border-gray-300 px-2 py-2">Quantity</th>
                <th className="border border-gray-300 px-2 py-2">Rate</th>
                <th className="border border-gray-300 px-2 py-2">Per</th>
                <th className="border border-gray-300 px-2 py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {details.map((item, i) => (
                <tr key={item.id}>
                  <td className="border border-gray-300 px-2 py-1">{i + 1}</td>
                  <td className="border border-gray-300 px-2 py-1 text-left">{item.endgoodName}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.boxes ?? item.packingDetailIds?.length ?? '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.userProvidedHeatNo || item.heatNo || '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.hsn ?? '—'}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.qty}</td>
                  <td className="border border-gray-300 px-2 py-1">{roundRate2(item.rate ?? 0).toFixed(2)}</td>
                  <td className="border border-gray-300 px-2 py-1">{item.uom ?? 'Nos'}</td>
                  <td className="border border-gray-300 px-2 py-1">
                    {lineAmountFromQtyRate(item.qty ?? 0, item.rate ?? 0).toFixed(2)}
                  </td>
                </tr>
              ))}
              <>
                <tr>
                  <td colSpan={5} className="border border-gray-300 px-2 py-1 text-right font-semibold"></td>
                  <td className="border border-gray-300"></td>
                  <td colSpan={2} className="border border-gray-300 text-right font-semibold"></td>
                  <td className="border border-gray-300 text-right font-semibold">{calc.taxableAmount.toFixed(2)}</td>
                </tr>
                {!isGst && (
                  <>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">CGST</td>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                      <td className="border border-gray-300 text-right font-semibold">{calc.totalCgst.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">SGST</td>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                      <td className="border border-gray-300 text-right font-semibold">{calc.totalSgst.toFixed(2)}</td>
                    </tr>
                  </>
                )}
                {isGst && (
                  <tr>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold">IGST</td>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold"></td>
                    <td className="border border-gray-300 text-right font-semibold">{calc.totalIgst.toFixed(2)}</td>
                  </tr>
                )}
                {otherChargesList.map((oc) => (
                  <tr key={oc.id}>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold">
                      {oc.chargeName ?? oc.chargeMaster?.chargeName ?? ''} ({oc.chargeDescription ?? oc.chargeMaster?.description ?? ''})
                    </td>
                    <td colSpan={4} className="border border-gray-300"></td>
                    <td className="border border-gray-300 text-right font-semibold">{(oc.amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={4} className="border border-gray-300 text-right py-2">TOTAL</td>
                  <td className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-center">{calc.totalQty}</td>
                  <td colSpan={2} className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-right">₹ {calc.totalAmount.toFixed(2)}</td>
                </tr>
              </>
            </tbody>
          </table>
        </div>
        ) : (
          <p className="text-sm text-gray-500 py-4">No line items.</p>
        )}

        <div className="mt-4 border-t pt-4">
          <p className="text-sm"><strong>Amount Chargeable (in words):</strong> INR {amountInWords(calc.totalAmount)}</p>
        </div>

        {/* HSN Summary */}
        <div className="mt-4 border rounded overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="border border-gray-300 p-2 text-center">HSN/SAC</th>
                <th className="border border-gray-300 p-2 text-center">Taxable Value</th>
                {!isGst && (
                  <>
                    <th className="border border-gray-300 p-2 text-center">CGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">CGST Amount</th>
                    <th className="border border-gray-300 p-2 text-center">SGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">SGST Amount</th>
                  </>
                )}
                {isGst && (
                  <>
                    <th className="border border-gray-300 p-2 text-center">IGST Rate</th>
                    <th className="border border-gray-300 p-2 text-center">IGST Amount</th>
                  </>
                )}
                <th className="border border-gray-300 p-2 text-center">Total Tax Amount</th>
              </tr>
            </thead>
            <tbody>
              {hsnWiseTaxDetails.map((row, idx) => (
                <tr key={idx} className="border-b">
                  <td className="border border-gray-300 p-2">{row.hsn}</td>
                  <td className="border border-gray-300 p-2">{row.taxableValue.toFixed(2)}</td>
                  {!isGst && (
                    <>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.cgstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.cgstAmount.toFixed(2)}</td>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.sgstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.sgstAmount.toFixed(2)}</td>
                    </>
                  )}
                  {isGst && (
                    <>
                      <td className="border border-gray-300 p-2 text-center">{toDisplayRate(row.igstRate).toFixed(2)}%</td>
                      <td className="border border-gray-300 p-2 text-right">{row.igstAmount.toFixed(2)}</td>
                    </>
                  )}
                  <td className="border border-gray-300 p-2 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="border border-gray-300 p-2">TOTAL</td>
                <td className="border border-gray-300 p-2">{calc.taxableAmount.toFixed(2)}</td>
                {!isGst && (
                  <>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalCgst.toFixed(2)}</td>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalSgst.toFixed(2)}</td>
                  </>
                )}
                {isGst && (
                  <>
                    <td className="border border-gray-300 p-2"></td>
                    <td className="border border-gray-300 p-2 text-right">{calc.totalIgst.toFixed(2)}</td>
                  </>
                )}
                <td className="border border-gray-300 p-2 text-right">{calc.totalTax.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 border rounded p-4">
          <p className="text-sm"><strong>Tax Amount (in words):</strong> INR {amountInWords(calc.totalTax)}</p>
          <p className="font-semibold mt-2">Company's PAN: {branchDetails?.panNo ?? 'NA'}</p>
        </div>
        <div className="mt-4 flex justify-between border-t pt-4">
          <div>
            <p className="font-semibold">Declaration:</p>
            <p className="text-sm text-gray-700">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">Authorized Signatory</p>
          </div>
        </div>
              </div>
            </div>
            {copyIndex < copies - 1 && (
              <div className="hidden print:block" style={{ pageBreakAfter: 'always' }} />
            )}
          </React.Fragment>
        );
      })}
      </div>
      )}

      {isEInvoiced && (
        <div className="w-full max-w-none min-w-0 overflow-x-auto print:overflow-visible">
          <InvoiceChunkedPrint
            invoice={invoice}
            branchDetails={branchDetails}
            shipToCustomer={shipToCustomer}
            billToCustomer={billToCustomer}
            details={details}
            otherChargesList={otherChargesList}
            calc={calc}
            hsnWiseTaxDetails={hsnWiseTaxDetails}
            isGst={isGst}
            qrValue={qrValue}
            copies={copies}
            showEwayBillOnView={showEwayBillOnView}
            digitallySignedByName={einvoiceSignerName}
            digitallySignedOn={digitallySignedOnDisplay}
            exportBranchLutDisplay={exportBranchLutDisplay}
            exportBranchLutLoading={exportBranchLutLoading}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceViewPage;
